import { ApplicationResourcesFormat } from '@/application/ApplicationResourcesFormat'
import { SimpleSpineAttachmentLoader } from '@/containers/common/SimpleSpineAttachmentLoader'
import {
    Spine,
    TextureAtlas,
    AtlasAttachmentLoader,
    SkeletonJson,
    type ITrackEntry,
} from '@esotericsoftware/spine-pixi-v8'
import * as PIXI from 'pixi.js'

export class SimpleSpine extends Spine {
    // ColorTransform rimosso - non necessario in PixiJS v8

    public constructor(name: string) {
        // PixiJS v8 with @esotericsoftware/spine-pixi-v8:
        // We need to manually create TextureAtlas and load skeleton

        const rawName = String(name || '')
        const normalizedName = rawName.replace(/^\//, '')
        const jsonPath = normalizedName.replace(/\.(json|skel)$/, '')
        const shortName = jsonPath.split('/').pop() || jsonPath

        const cacheStore: any = (PIXI as any)?.Assets?.cache?._cache
        const hasInCache = (key: string): boolean => {
            if (!cacheStore) return false
            if (typeof cacheStore.has === 'function') return cacheStore.has(key)
            return Object.prototype.hasOwnProperty.call(cacheStore, key)
        }
        const getFromCache = (key: string): any => {
            if (!cacheStore) return undefined
            if (typeof cacheStore.get === 'function') return cacheStore.get(key)
            return cacheStore[key]
        }
        const resolveAsset = (candidates: string[]): any => {
            const unique = Array.from(new Set(candidates.filter(Boolean)))

            for (const key of unique) {
                if (hasInCache(key)) {
                    return getFromCache(key)
                }
            }

            for (const key of unique) {
                const asset = PIXI.Assets.get(key)
                if (asset != null) return asset
            }

            return null
        }

        const getCacheKeys = (): string[] => {
            if (!cacheStore) return []
            if (typeof cacheStore.keys === 'function') return Array.from(cacheStore.keys())
            return Object.keys(cacheStore)
        }

        const resolveAssetBySuffix = (suffix: string): any => {
            if (!suffix) return null

            const keys = getCacheKeys()
            const directKey = keys.find((k: string) => k === suffix)
            if (directKey) {
                return getFromCache(directKey) ?? PIXI.Assets.get(directKey)
            }

            const endWith = keys.find((k: string) => String(k).endsWith(`/${suffix}`) || String(k).endsWith(suffix))
            if (endWith) {
                return getFromCache(endWith) ?? PIXI.Assets.get(endWith)
            }

            return null
        }

        const unwrapAssetData = (asset: any): any => {
            if (asset == null) return asset
            if (typeof asset !== 'object') return asset

            if (asset.data != null) return asset.data
            if (asset.resource != null) return unwrapAssetData(asset.resource)
            if (asset.source != null) return unwrapAssetData(asset.source)
            if (asset.value != null) return unwrapAssetData(asset.value)

            return asset
        }

        const toText = (asset: any): string => {
            const value = unwrapAssetData(asset)

            if (typeof value === 'string') return value
            if (value instanceof ArrayBuffer) return new TextDecoder().decode(new Uint8Array(value))
            if (ArrayBuffer.isView(value)) return new TextDecoder().decode(value as ArrayBufferView as Uint8Array)

            return ''
        }

        // Get the JSON data
        const jsonResource = resolveAsset([
            rawName,
            normalizedName,
            jsonPath,
            `${jsonPath}.json`,
            shortName,
            `${shortName}.json`,
        ])
        const jsonData = unwrapAssetData(jsonResource)
        if (!jsonData) {
            throw new Error(`Spine JSON resource "${name}" not found in cache`)
        }
        let atlasResource = resolveAsset([
            `${shortName}.atlas`,
            `/${shortName}.atlas`,
            `${jsonPath}.atlas`,
            `/${jsonPath}.atlas`,
        ])
        if (!atlasResource) {
            atlasResource = resolveAssetBySuffix(`${shortName}.atlas`) || resolveAssetBySuffix(`${jsonPath}.atlas`)
        }

        const atlasCandidate = unwrapAssetData(atlasResource)
        const atlasText = toText(atlasCandidate)

        let atlas: TextureAtlas | null = null

        if (atlasCandidate instanceof TextureAtlas) {
            atlas = atlasCandidate
        } else if (atlasText) {
            // spine-pixi-v8 uses an internal texture loader; text-only constructor is expected
            atlas = new TextureAtlas(atlasText)
        }

        if (!atlas) {
            const available = getCacheKeys()
            console.error('[SimpleSpine] Atlas not found! Available assets:', available)
            throw new Error(`Spine atlas for "${name}" not found.`)
        }

        // Get the base texture
        let baseTexture = unwrapAssetData(
            resolveAsset([`${shortName}.png`, `/${shortName}.png`, `${jsonPath}.png`, `/${jsonPath}.png`])
        )
        if (!baseTexture) {
            baseTexture = unwrapAssetData(
                resolveAssetBySuffix(`${shortName}.png`) || resolveAssetBySuffix(`${jsonPath}.png`)
            )
        }
        if (!baseTexture) {
            const available = getCacheKeys()
            console.error('[SimpleSpine] PNG not found! Available assets:', available)
            throw new Error(`Spine texture for "${name}" not found.`)
        }

        // Create skeleton using atlas
        const atlasLoader = new AtlasAttachmentLoader(atlas)
        const skeletonJson = new SkeletonJson(atlasLoader)
        const skeletonData = skeletonJson.readSkeletonData(jsonData)

        super(skeletonData)
    }

    public seek(label: string, progress: number) {
        const animation = this.skeleton.data.findAnimation(label)
        const track = this.state.setAnimation(0, label, false)
        track.trackTime = animation.duration * progress
    }

    public play(label: string, loop: boolean = false): ITrackEntry {
        this.lastTime = Date.now()

        const track = this.state.setAnimation(0, label, loop)
        this.update(0.001)

        return track
    }

    public queue(label: string, loop: boolean = false): ITrackEntry {
        return this.state.addAnimation(0, label, loop, 0)
    }

    public stop(): void {
        this.skeleton.setToSetupPose()
        this.state.clearTracks()
    }
}
